import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

export interface AgentStatus {
  agentEnabled: boolean;
  agentEnabledAt: string | null;
  agentPaused: boolean;
  pausedAt: string | null;
  resumedAt: string | null;
  lastReconciledAt: string | null;
  autoCreateSubscriptions: boolean;
  hubspotWriteBackEnabled: boolean;
}

export interface Prerequisites {
  hubspotConnected: boolean;
  stripeConnected: boolean;
  settingsExist: boolean;
  canEnable: boolean;
}

export interface RevSyncSettings {
  // Read-only status fields
  agentEnabled: boolean;
  agentEnabledAt: string | null;
  agentPaused: boolean;
  lastReconciledAt: string | null;
  // Customer Lifecycle Stage
  customerLifecycleStage: string | null;
  // Toggle 1
  autoCreateSubscriptions: boolean;
  subscriptionProductMapping?: {
    enabled: boolean;
    defaultProductId?: string;
    rules?: Array<Record<string, unknown>>;
  };
  subscriptionSetup?: {
    fieldMappings?: {
      startDate?: { field?: string };
      endDate?: { field?: string };
    };
  };
  subscriptionBilling?: {
    collectionMethod: "charge_automatically" | "send_invoice";
    daysUntilDue?: number;
    prorationBehavior: "none" | "create_prorations" | "always_invoice";
  } | null;
  netNewDealPipelineId: string | null;
  netNewDealStageId: string | null;
  // Toggle 2
  enableRenewalDeals: boolean;
  renewalPipelineId: string | null;
  renewalStageId: string | null;
  // Note: Contracts are automatically created when subscriptions are created
  closedWonPipelineId: string | null;
  closedWonStageIds: string[];
  // Toggle 4 (Enterprise only)
  syncContractsToHubSpot: boolean;
  // Toggle 5
  cSuiteDailyReportEnabled: boolean;
  // Enterprise check
  hubspotEnterpriseCapable: boolean;
  // Legacy (hide from UI)
  hubspotWriteBackEnabled: boolean;
}

export interface PipelineStage {
  id: string;
  label: string;
  closedWon: boolean;
  probability: number;
}

export interface Pipeline {
  id: string;
  label: string;
  stages: PipelineStage[];
}

export interface AISuggestion {
  pipelineId: string;
  pipelineLabel: string;
  stageId: string;
  stageLabel: string;
  confidence: "high" | "medium" | "low";
  reasoning: string;
}

export interface RenewalSuggestion {
  sourcePipeline: {
    pipelineId: string;
    pipelineLabel: string;
    confidence: "high" | "medium" | "low";
    reasoning: string;
  } | null;
  destinationPipeline: {
    pipelineId: string;
    pipelineLabel: string;
    confidence: "high" | "medium" | "low";
    reasoning: string;
  } | null;
  destinationStage: {
    pipelineId: string;
    pipelineLabel: string;
    stageId: string;
    stageLabel: string;
    confidence: "high" | "medium" | "low";
    reasoning: string;
  } | null;
}

export interface SubscriptionSetupSuggestion {
  useProductLibrary?: boolean;
  defaultProductId?: string;
  defaultProductName?: string;
  fieldMappings?: {
    amount?: { field: string; confidence: "high" | "medium" | "low" };
    billingCycle?: { field: string; confidence: "high" | "medium" | "low" };
    startDate?: { field: string; confidence: "high" | "medium" | "low" };
    currency?: { field: string; confidence: "high" | "medium" | "low" };
  };
}

export interface SettingsSuggestions {
  netNewDeal: AISuggestion | null;
  // contractCreation removed - contracts are automatic with subscriptions
  renewalDeal: RenewalSuggestion;
  subscriptionSetup?: SubscriptionSetupSuggestion;
  provider: "openai" | "gemini" | "anthropic" | "fallback";
}

export interface SettingsWithSuggestions {
  settings: RevSyncSettings;
  pipelines: Pipeline[];
  suggestions: SettingsSuggestions;
  isFirstTimeSetup: boolean;
  userMessage?: string;
  dealDateFields?: Array<{
    name: string;
    label: string;
    fieldType: "date" | "datetime";
  }>;
}

export interface UpdateSettingsRequest {
  customerLifecycleStage?: string | null;
  autoCreateSubscriptions?: boolean;
  subscriptionProductMapping?: {
    enabled: boolean;
    defaultProductId?: string;
    rules?: Array<Record<string, unknown>>;
  };
  subscriptionSetup?: {
    fieldMappings?: {
      startDate?: { field?: string };
      endDate?: { field?: string };
    };
  };
  subscriptionBilling?: {
    collectionMethod: "charge_automatically" | "send_invoice";
    daysUntilDue?: number;
    prorationBehavior: "none" | "create_prorations" | "always_invoice";
  } | null;
  netNewDealPipelineId?: string | null;
  netNewDealStageId?: string | null;
  enableRenewalDeals?: boolean;
  renewalPipelineId?: string | null;
  renewalStageId?: string | null;
  // autoCreateContractOnClosedWon removed - contracts are automatic with subscriptions
  syncContractsToHubSpot?: boolean;
  cSuiteDailyReportEnabled?: boolean;
}

export interface LifecycleStage {
  value: string;
  label: string;
}

export interface LifecycleStagesResponse {
  lifecycleStages: LifecycleStage[];
  currentSelection: string;
  suggestedStage: string | null;
  suggestionReasoning: string | null;
  hasCustomerStage: boolean;
}

export interface UpdateLifecycleStageRequest {
  lifecycleStage: string;
}

export interface UpdateLifecycleStageResponse {
  success: boolean;
  customerLifecycleStage: string;
  message: string;
  userMessage: string;
}

export interface Reconciliation {
  id: string;
  mode: "auto" | "incremental" | "backfill";
  subscriptionsScanned: number;
  contractsUpserted: number;
  errors: string[];
  startedAt: string;
  completedAt: string | null;
  status: "running" | "completed" | "failed";
}

export interface ReconciliationHistory {
  reconciliations: Reconciliation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface RetentionMonthlyMetric {
  month: string;
  startingArr: number;
  endingArr: number;
  expansionArr: number;
  contractionArr: number;
  churnArr: number;
  grr: number | null;
  nrr: number | null;
  cohortContracts: number;
  renewedContracts: number;
  churnedContracts: number;
}

export interface RetentionTtmMetric {
  startingArr: number;
  endingArr: number;
  expansionArr: number;
  contractionArr: number;
  churnArr: number;
  grr: number | null;
  nrr: number | null;
  cohortContracts: number;
  renewedContracts: number;
  churnedContracts: number;
}

export interface RetentionMetricsResponse {
  monthly: RetentionMonthlyMetric[];
  ttm: RetentionTtmMetric;
}

export interface TriggerReconciliationResponse {
  success: boolean;
  mode: "auto" | "incremental" | "backfill";
  message: string;
  userMessage?: string;
  startedAt: string;
}

export interface ContractCustomer {
  id: string;
  globalId: string;
  name: string | null;
  email: string | null;
  hubspotContactId: string | null;
  hubspotCompanyId: string | null;
}

export interface Contract {
  id: string;
  contractName: string | null;
  contractType: "new" | "existing";
  dealType?: "New Business" | "Existing Business" | "Renewal";
  status: "active" | "past";
  mrr: number;
  arr: number;
  tcv: number;
  acv: number;
  renewableBase?: number; // Base amount that is renewable (industry standard: equals ARR)
  startDate: string;
  endDate: string | null;
  billingCycle: "monthly" | "annual" | "quarterly" | null;
  productName?: string | null;
  priorContractId?: string; // Link to original contract if this is a renewal
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  hubspotDealId: string | null;
  hubspotDealUrl?: string | null;
  isHistorical: boolean;
  source:
    | "webhook"
    | "stripe_reconcile"
    | "stripe_backfill"
    | "automated"
    | null;
  customerName?: string | null; // Legacy field, may be deprecated
  customerEmail?: string | null; // Legacy field, may be deprecated
  createdAt: string;
  updatedAt: string;
  urls?: ContractUrls;
  
  // Renewal-specific fields (only populated for renewal contracts)
  renewalTCV?: number;
  renewalACV?: number;
  renewalARR?: number;
  renewalMRR?: number;
  renewalServices?: string;
  renewalUpsell?: number;
  renewalDownsell?: number;
  
  // Linked contracts (renewals)
  linkedContracts?: LinkedContract[];
  
  // Original contract (if this is a renewal)
  originalContract?: OriginalContract;
}

export interface ContractUrls {
  hubspot?: {
    deal?: string;
    contact?: string;
    company?: string;
  };
  stripe?: {
    subscription?: string;
    customer?: string;
  };
}

// Linked contract (used in linkedContracts array)
export interface LinkedContract {
  id: string;
  contractName: string;
  contractType: "new" | "existing";
  dealType?: "New Business" | "Existing Business" | "Renewal";
  status: "active" | "past";
  startDate: string;
  endDate: string | null;
  mrr: number;
  arr: number;
  tcv: number;
  acv: number;
  billingCycle: "monthly" | "annual" | "quarterly" | null;
  productName?: string | null;
  stripeSubscriptionId?: string | null;
  hubspotDealId?: string | null;
  priorContractId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Original contract (used when viewing a renewal)
export interface OriginalContract {
  id: string;
  contractName: string;
  contractType: "new" | "existing";
  dealType?: "New Business" | "Existing Business" | "Renewal";
  status: "active" | "past";
  startDate: string;
  endDate: string | null;
  mrr: number;
  arr: number;
  tcv: number;
  acv: number;
  billingCycle: "monthly" | "annual" | "quarterly" | null;
  productName?: string | null;
  stripeSubscriptionId?: string | null;
  hubspotDealId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContractDetail extends Contract {
  // Customer Information
  customer: ContractCustomer | null;
  
  // Contract Details
  quantity: number | null;
  
  // Additional Metadata
  paymentTerms: string | null;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  dealOwnerEmail: string | null;
  
  // HubSpot Integration
  hubspotPortalId: string | null;
  hubspotCompanyId: string | null;
  hubspotContactId: string | null;
  
  // Linked contracts (renewals and original)
  linkedContracts?: LinkedContract[];
  
  // Original contract (if this is a renewal)
  originalContract?: OriginalContract;
}

export interface ContractPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ContractListResponse {
  contracts: Contract[];
  pagination: ContractPagination;
}

export interface ContractFilters {
  page?: number;
  limit?: number;
  status?: "active" | "past";
  contractType?: "new" | "existing";
  dealType?: "New Business" | "Existing Business" | "Renewal";
  globalCustomerId?: string;
  billingCycle?: "monthly" | "annual" | "quarterly";
  search?: string; // Search by contract name, product name, or deal owner email
  isHistorical?: boolean;
  hubspotLinked?: boolean;
  sortBy?:
    | "createdAt"
    | "updatedAt"
    | "startDate"
    | "endDate"
    | "mrr"
    | "arr"
    | "status";
  sortOrder?: "asc" | "desc";
}

export class RevSyncService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/revsync";
  }

  async getAgentStatus(
    config?: AxiosRequestConfig
  ): Promise<{ data: AgentStatus; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<AgentStatus>>(
        `${this.baseUrl}/agent/status`,
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch agent status.";
      throw new Error(errorMessage);
    }
  }

  async checkPrerequisites(): Promise<Prerequisites> {
    try {
      const [hubspotStatus, stripeStatus, settings] = await Promise.allSettled([
        httpService.get<
          ApiResponse<{ connected: boolean; hubspotInstanceId?: string }>
        >("/hubspot/status"),
        httpService.get<ApiResponse<{ connected: boolean }>>("/stripe/status"),
        httpService
          .get<ApiResponse<any>>(`${this.baseUrl}/settings`)
          .catch(() => null),
      ]);

      const hubspotData =
        hubspotStatus.status === "fulfilled" ? hubspotStatus.value.data : null;
      const stripeData =
        stripeStatus.status === "fulfilled" ? stripeStatus.value.data : null;
      const settingsData =
        settings.status === "fulfilled" && settings.value
          ? settings.value.data
          : null;

      const hubspotConnected =
        hubspotData?.connected === true && !!hubspotData?.hubspotInstanceId;
      const stripeConnected = stripeData?.connected === true;
      const settingsExist = !!settingsData;

      return {
        hubspotConnected,
        stripeConnected,
        settingsExist,
        canEnable: hubspotConnected && stripeConnected,
      };
    } catch (error: any) {
      console.error("Error checking prerequisites:", error);
      return {
        hubspotConnected: false,
        stripeConnected: false,
        settingsExist: false,
        canEnable: false,
      };
    }
  }

  async enableAgent(config?: AxiosRequestConfig): Promise<{
    data: { success: boolean; agentEnabled: boolean; agentEnabledAt: string };
    message: string;
    userMessage?: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<{
          success: boolean;
          agentEnabled: boolean;
          agentEnabledAt: string;
          userMessage?: string;
        }>
      >(`${this.baseUrl}/agent/enable`, {}, config);
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data?.userMessage,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to enable agent.";
      throw new Error(errorMessage);
    }
  }

  async pauseAgent(config?: AxiosRequestConfig): Promise<{
    data: { success: boolean; agentPaused: boolean; pausedAt: string };
    message: string;
    userMessage?: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<{
          success: boolean;
          agentPaused: boolean;
          pausedAt: string;
          userMessage?: string;
        }>
      >(`${this.baseUrl}/agent/pause`, {}, config);
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data?.userMessage,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to pause agent.";
      throw new Error(errorMessage);
    }
  }

  async resumeAgent(config?: AxiosRequestConfig): Promise<{
    data: {
      success: boolean;
      agentPaused: boolean;
      resumedAt: string;
      reconciliationTriggered: boolean;
      userMessage?: string;
    };
    message: string;
    userMessage?: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<{
          success: boolean;
          agentPaused: boolean;
          resumedAt: string;
          reconciliationTriggered: boolean;
          userMessage?: string;
        }>
      >(`${this.baseUrl}/agent/resume`, {}, config);
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data?.userMessage,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to resume agent.";
      throw new Error(errorMessage);
    }
  }

  async getSettings(
    config?: AxiosRequestConfig
  ): Promise<{ data: SettingsWithSuggestions; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<SettingsWithSuggestions>>(
        `${this.baseUrl}/settings`,
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch settings.";
      throw new Error(errorMessage);
    }
  }

  async updateSettings(
    settings: UpdateSettingsRequest,
    config?: AxiosRequestConfig
  ): Promise<{ data: RevSyncSettings; message: string; userMessage?: string; productLibraryAutoEnabled?: boolean; productCount?: number; defaultProductName?: string }> {
    try {
      if (settings.autoCreateSubscriptions) {
        if (!settings.netNewDealPipelineId || !settings.netNewDealStageId) {
          throw new Error(
            "Pipeline and Stage are required when auto-create is enabled"
          );
        }
      }

      if (settings.enableRenewalDeals) {
        if (!settings.renewalPipelineId || !settings.renewalStageId) {
          throw new Error(
            "Renewal Pipeline and Stage are required when renewals are enabled"
          );
        }
      }

      const cleanedSettings = Object.entries(settings).reduce(
        (acc, [key, value]) => {
          acc[key] = value === "" ? null : value;
          return acc;
        },
        {} as Record<string, any>
      );

      const response = await httpService.put<ApiResponse<RevSyncSettings & { userMessage?: string; productLibraryAutoEnabled?: boolean; productCount?: number; defaultProductName?: string }>>(
        `${this.baseUrl}/settings`,
        cleanedSettings,
        config
      );
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data.userMessage,
        productLibraryAutoEnabled: response.data.productLibraryAutoEnabled,
        productCount: response.data.productCount,
        defaultProductName: response.data.defaultProductName,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update settings.";
      throw new Error(errorMessage);
    }
  }

  async getSettingsWithSuggestions(
    forceSuggestions: boolean = false,
    config?: AxiosRequestConfig
  ): Promise<{ data: SettingsWithSuggestions; message: string }> {
    const params = forceSuggestions ? { forceSuggestions: "true" } : {};
    return this.getSettings({
      ...config,
      params: { ...config?.params, ...params },
    });
  }

  async getLifecycleStages(
    config?: AxiosRequestConfig
  ): Promise<{ data: LifecycleStagesResponse; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<LifecycleStagesResponse>>(
        `${this.baseUrl}/settings/lifecycle-stages`,
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch lifecycle stages.";
      throw new Error(errorMessage);
    }
  }

  async updateCustomerLifecycleStage(
    lifecycleStage: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: UpdateLifecycleStageResponse; message: string }> {
    try {
      const response = await httpService.put<ApiResponse<UpdateLifecycleStageResponse>>(
        `${this.baseUrl}/settings/customer-lifecycle-stage`,
        { lifecycleStage },
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update customer lifecycle stage.";
      throw new Error(errorMessage);
    }
  }

  async getReconciliationHistory(
    page: number = 1,
    limit: number = 50,
    config?: AxiosRequestConfig
  ): Promise<{ data: ReconciliationHistory; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<ReconciliationHistory>
      >(`${this.baseUrl}/reconcile/history`, {
        ...config,
        params: { page, limit },
      });
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch reconciliation history.";
      throw new Error(errorMessage);
    }
  }

  async triggerReconciliation(
    mode?: "auto" | "incremental" | "backfill",
    config?: AxiosRequestConfig
  ): Promise<{
    data: TriggerReconciliationResponse;
    message: string;
    userMessage?: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<TriggerReconciliationResponse>
      >(
        `${this.baseUrl}/reconcile`,
        mode ? { mode } : {},
        config
      );
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data?.userMessage,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to trigger reconciliation.";
      throw new Error(errorMessage);
    }
  }

  async listContracts(
    filters: ContractFilters = {},
    config?: AxiosRequestConfig
  ): Promise<{ data: ContractListResponse; message: string }> {
    try {
      const params: Record<string, string | number> = {
        page: filters.page || 1,
        limit: filters.limit || 50,
      };

      if (filters.status) {
        params.status = filters.status;
      }

      if (filters.contractType) {
        params.contractType = filters.contractType;
      }

      if (filters.dealType) {
        params.dealType = filters.dealType;
      }

      if (filters.globalCustomerId) {
        params.globalCustomerId = filters.globalCustomerId;
      }

      if (filters.billingCycle) {
        params.billingCycle = filters.billingCycle;
      }

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.isHistorical !== undefined) {
        params.isHistorical = String(filters.isHistorical);
      }

      if (filters.hubspotLinked !== undefined) {
        params.hubspotLinked = String(filters.hubspotLinked);
      }

      if (filters.sortBy) {
        params.sortBy = filters.sortBy;
      }

      if (filters.sortOrder) {
        params.sortOrder = filters.sortOrder;
      }

      const response = await httpService.get<ApiResponse<ContractListResponse>>(
        `${this.baseUrl}/contracts`,
        {
          ...config,
          params,
        }
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch contracts.";
      throw new Error(errorMessage);
    }
  }

  async getContract(
    contractId: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: ContractDetail; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<{ contract: ContractDetail }>>(
        `${this.baseUrl}/contracts/${contractId}`,
        config
      );
      return {
        data: response.data.contract,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch contract.";
      throw new Error(errorMessage);
    }
  }

  async getRetentionMetrics(
    months = 12,
    config?: AxiosRequestConfig
  ): Promise<{ data: RetentionMetricsResponse; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<RetentionMetricsResponse>>(
        `${this.baseUrl}/metrics/retention`,
        {
          ...config,
          params: { months },
        }
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch retention metrics.";
      throw new Error(errorMessage);
    }
  }

  async clearData(config?: AxiosRequestConfig): Promise<{
    data: {
      deleted: {
        contracts: number;
        globalCustomers: number;
        reconciliationRuns: number;
        fieldMappings: number;
        hubSpotObjectMappings: number;
        renewalReminders: number;
        hubSpotSyncLogs: number;
      };
      settingsReset: boolean;
      agentUninstalled: boolean;
    };
    message: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<{
          deleted: {
            contracts: number;
            globalCustomers: number;
            reconciliationRuns: number;
            fieldMappings: number;
            hubSpotObjectMappings: number;
            renewalReminders: number;
            hubSpotSyncLogs: number;
          };
          settingsReset: boolean;
          agentUninstalled: boolean;
        }>
      >(`${this.baseUrl}/clear-data`, {}, config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.response?.data?.error || "Failed to clear data.";
      throw new Error(errorMessage);
    }
  }
}

export const revSyncService = new RevSyncService();
